<template>
  <ContentWrapperControl>
    <!-- Header -->
    <div
      class="bg-white p-4 rounded-b shadow overflow-hidden mb-4 text-sm leading-normal text-grey-darkest"
    >
      Join us for
      <strong class="text-primary">PAPSI Run: #FortyFOURWARDofHOPE</strong> as
      we celebrate PAFCPIC's 44th Anniversary on
      <strong>November 15, 2025</strong>, at the
      <strong>Philippine Army Grandstand</strong>, Fort Bonifacio.

      <div class="my-4 h-px bg-grey-light"></div>

      <p class="mt-2">{{ this.lang("lbl_0000341") }}</p>
    </div>

    <!-- Registrants -->
    <div
      v-for="(registrant, index) in registrants"
      :key="index"
      class="bg-white p-4 rounded shadow overflow-hidden mb-4"
    >
      <!-- Summary View -->
      <div v-if="editing_index !== index" class="flex flex-col">
        <div class="flex justify-between items-start">
          <div class="text-primary text-sm">
            <strong>{{ this.lang("lbl_0000322") + (index + 1) }}</strong>
          </div>
          <div class="flex items-center">
            <img
              v-show="!show_form"
              src="@/assets/icons/papsi_edit.svg"
              class="w-5 h-5 mr-2 cursor-pointer"
              @click="editRegistrant(index)"
              alt="Edit"
            />
            <img
              v-show="!show_form"
              src="@/assets/icons/papsi_delete.svg"
              class="w-5 h-5 cursor-pointer"
              @click="deleteRegistrant(index)"
              alt="Delete"
            />
          </div>
        </div>

        <!-- Registrant details -->
        <div class="text-sm mt-2">
          <div class="uppercase">
            <strong>{{ registrant.lname }}, {{ registrant.fname }}</strong>
          </div>
          <div class="mt-2 text-grey-dark">
            Category:
            {{
              registrant.category === "2"
                ? getLabel(getRaceCategories, registrant.category)
                : `${getLabel(
                    getRaceCategories,
                    registrant.category
                  )} (${getDistanceLabel(
                    registrant.segment,
                    registrant.distance
                  )})`
            }}
          </div>
        </div>
      </div>

      <!-- Form View -->
      <div v-else-if="editing_index === index">
        <div class="mb-4">
          <FormTextControl
            v-model="registrant.lname"
            name="lname"
            :placeholder="lang('lbl_0000145')"
            :max_length="24"
            :uppercase="true"
            required
          />
        </div>
        <div class="mb-4">
          <FormTextControl
            v-model="registrant.fname"
            name="fname"
            :placeholder="lang('lbl_0000146')"
            :max_length="24"
            :uppercase="true"
            required
          />
        </div>
        <div class="mb-4">
          <FormTextControl
            v-model="registrant.mname"
            name="mname"
            :placeholder="lang('lbl_0000325')"
            :max_length="24"
            :uppercase="true"
          />
        </div>
        <!-- Account Number -->
        <div class="mb-4">
          <FormTextControl
            v-model="registrant.account_no"
            name="account_no"
            :placeholder="lang('lbl_0000093')"
            :max_length="6"
            alphanumeric
            uppercase
            :disabled="
              registrant.segment !== null &&
              registrant.segment !== undefined &&
              registrant.segment !== '' &&
              !['EMP', 'MEM', 'DEP'].includes(registrant.segment)
            "
          />
        </div>

        <!-- Serial Number -->
        <div class="mb-4">
          <FormTextControl
            v-model="registrant.serial_no"
            name="serial_no"
            :placeholder="lang('lbl_0000094')"
            :max_length="7"
            alphanumeric
            uppercase
            :disabled="
              registrant.segment !== null &&
              registrant.segment !== undefined &&
              registrant.segment !== '' &&
              !['EMP', 'MEM', 'DEP'].includes(registrant.segment)
            "
          />
        </div>

        <div class="mb-4">
          <FormTextControl
            v-model="registrant.age"
            name="age"
            :placeholder="lang('lbl_0000103')"
            :max_length="2"
            required
          />
        </div>
        <div class="mb-4">
          <FormTextControl
            v-model="registrant.email"
            name="email"
            :placeholder="lang('lbl_0000005')"
            type="email"
            :max_length="50"
            required
          />
        </div>
        <div class="mb-4">
          <FormTextControl
            v-model="registrant.mobile"
            name="mobile"
            :placeholder="lang('lbl_0000007')"
            type="mobile"
            :max_length="10"
            required
          />
        </div>
        <div class="mb-4">
          <ComboboxControl
            name="gender"
            placeholder="Gender"
            :options="getGenders"
            v-model="registrant.gender"
            required
          />
        </div>

        <!-- Segment -->
        <div class="mb-4">
          <ComboboxControl
            name="segment"
            placeholder="Segment"
            :options="filteredSegments"
            v-model="registrant.segment"
            required
            :disabled="!registrant.age"
            @update:modelValue="clearSegmentRelatedFields(registrant)"
          >
          </ComboboxControl>
        </div>

        <!-- Branch Service / Office -->
        <div class="mb-4" v-if="showBranchService(registrant.segment)">
          <ComboboxControl
            name="branch_service"
            :placeholder="
              registrant.segment === 'EMP' ? 'Office' : 'Branch Service'
            "
            :options="getBranchServicesBySegment(registrant.segment)"
            v-model="registrant.branch_service"
            required
          />
        </div>

        <!-- Sponsor Type -->
        <div class="mb-4" v-if="showSponsorType(registrant.segment)">
          <ComboboxControl
            name="sponsor_type"
            placeholder="Sponsor Type"
            :options="getSponsorTypes()"
            v-model="registrant.sponsor_type"
            required
          />
        </div>

        <!-- Category -->
        <div class="mb-4">
          <ComboboxControl
            name="category"
            placeholder="Category"
            :options="getRaceCategories"
            v-model="registrant.category"
            required
          />
        </div>

        <!-- Distance -->
        <ComboboxControl
          name="distance"
          placeholder="Distance"
          :options="getDistanceBySegment(registrant.segment)"
          v-model="registrant.distance"
          :required="registrant.category !== '2'"
          :disabled="registrant.category === '2' || !registrant.segment"
        ></ComboboxControl>

        <!-- Waiver -->
        <div
          @click="!isWaiverDisabled(registrant) && openWaiver(index)"
          :class="[
            'text-sm font-medium rounded py-3 px-3 mt-4 text-left flex items-center gap-2 transition-colors duration-200',
            registrant.has_signed_waiver
              ? 'bg-primary-light text-primary'
              : isWaiverDisabled(registrant)
              ? 'bg-grey-lighter text-grey cursor-not-allowed'
              : 'bg-grey-lighter text-grey-darkest hover:bg-grey cursor-pointer',
          ]"
          :title="isWaiverDisabled(registrant) ? lang('lbl_0000340') : ''"
        >
          <svg
            v-if="registrant.has_signed_waiver"
            xmlns="http://www.w3.org/2000/svg"
            class="w-4 h-4 mr-2 text-primary"
            viewBox="0 0 20 20"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <rect
              x="1.5"
              y="1.5"
              width="17"
              height="17"
              rx="4"
              ry="4"
              fill="white"
              stroke="currentColor"
            />
            <polyline
              points="5 11 9 15 17 7"
              stroke="currentColor"
              stroke-width="2"
              fill="none"
            />
          </svg>
          {{
            lang(
              registrant.has_signed_waiver
                ? "lbl_0000338"
                : isWaiverDisabled(registrant)
                ? "lbl_0000340"
                : "lbl_0000337"
            )
          }}
        </div>
      </div>
    </div>

    <!-- Buttons -->
    <div class="flex flex-row items-center mt-4">
      <template v-if="show_form">
        <!-- Cancel Button -->
        <ButtonControl class="mr-4" outline @click="cancelEdit">
          {{ lang("lbl_0000343") || "Cancel" }}
        </ButtonControl>

        <!-- Save Button -->
        <ButtonControl
          @click="saveChanges(editing_index ?? registrants.length - 1)"
          :disabled="
            !currentRegistrant ||
            !isFormComplete(currentRegistrant) ||
            !currentRegistrant.has_signed_waiver ||
            parseInt(currentRegistrant.age) < 12
          "
        >
          {{ lang("lbl_0000320") }}
        </ButtonControl>
      </template>

      <template v-else-if="editing_index === null">
        <ButtonControl
          :outline="true"
          :disabled="!canAddRegistrant"
          @click="addRegistrant"
        >
          {{ lang("lbl_0000320") }}
        </ButtonControl>
        <ButtonControl
          class="ml-4"
          :disabled="registrants.length === 0 || !canRegisterAll"
          @click="registerAll"
        >
          {{ lang("lbl_0000033") }}
        </ButtonControl>
      </template>
    </div>
  </ContentWrapperControl>
  <DataPrivacyModal
    v-if="show_data_privacy"
    :show="show_data_privacy"
    @closed="onPolicyClosed"
    @agreed="show_data_privacy = false"
  />
  <FunRunWaiverControl
    v-if="show_waiver"
    :show="show_waiver"
    :waiver="registrants[current_waiver_index].waiver"
    :is_minor="registrants[current_waiver_index].age < 18"
    @agreed="onWaiverAgreed"
    @closed="onWaiverClosed"
  />
</template>

<script>
import ContentWrapperControl from "@/components/controls/ContentWrapperControl.vue";
import FunRunWaiverControl from "@/components/controls/FunRunWaiverControl.vue";
import DataPrivacyModal from "@/components/controls/DataPrivacyModal.vue";
import FormTextControl from "@/components/controls/FormTextControl.vue";
import ComboboxControl from "@/components/controls/ComboboxControl.vue";
import ButtonControl from "@/components/controls/ButtonControl.vue";
import mixin from "./../../mixin.js";
import FunRun from "@/services/FunRun.js";

export default {
  mixins: [mixin],
  emits: [
    "success",
    "error",
    "alert",
    "login",
    "logout",
    "showloader",
    "hideloader",
    "showblinder",
    "hideblinder",
    "terminate",
    "checkauth",
    "nofooter",
    "shownavbar",
    "hidenavbar",
    "opensidebar",
  ],
  components: {
    ContentWrapperControl,
    FunRunWaiverControl,
    DataPrivacyModal,
    FormTextControl,
    ComboboxControl,
    ButtonControl,
  },
  beforeRouteLeave(to, from, next) {
    const reg = this.local_registrants[this.editing_index];
    if (reg && !this.isFormComplete(reg)) {
      this.local_registrants.splice(this.editing_index, 1);
    }
    next();
  },

  mounted() {
    this.$emit("checkauth");
    this.$emit("shownavbar", {
      title: this.lang("lbl_0000316"),
      show: true,
      back: true,
    });
    this.$emit("nofooter", false);

    // Disable pull-to-refresh
    this.$store.state.is_pdf_open = true;

    if (this.$store.state.fun_run.registrants?.length > 0) {
      this.local_registrants = [...this.$store.state.fun_run.registrants];
      this.editing_index = null;
      this.local_registrants.forEach((r) => (r.is_summary = true));
      this.show_data_privacy = false;
    }

    this.fetchSegments();
  },

  data() {
    return {
      local_registrants: [],
      show_data_privacy: true,
      show_waiver: false,
      editing_index: null,
      segments: [],
      has_registered: false,
      timestamp: null,
    };
  },
  computed: {
    registrants: {
      get() {
        return this.local_registrants;
      },
      set(value) {
        this.local_registrants = value;
      },
    },
    show_form() {
      return this.editing_index !== null;
    },
    getSegments() {
      return this.segments;
    },
    getGenders() {
      return [
        { label: "Male", value: "M" },
        { label: "Female", value: "F" },
        { label: "Rather not say", value: "X" },
      ];
    },
    getRaceDistance() {
      return [
        { label: "3 KM", value: "03k" },
        { label: "5 KM", value: "05k" },
        { label: "10 KM", value: "10k" },
      ];
    },
    getRaceCategories() {
      return [
        { label: "Fun Run", value: "1" },
        { label: "Zumba", value: "2" },
        { label: "Both Fun Run & Zumba", value: "3" },
      ];
    },
    canAddRegistrant() {
      const last = this.registrants[this.registrants.length - 1];
      if (!this.registrants.length) return true;
      if (!last) return false;
      return this.isFormComplete(last);
    },
    canRegisterAll() {
      return (
        this.registrants.length &&
        this.registrants.every(
          (r) => this.isFormComplete(r) && r.has_signed_waiver
        )
      );
    },
    currentRegistrant() {
      const idx =
        this.editing_index != null
          ? this.editing_index
          : this.registrants.length
          ? this.registrants.length - 1
          : null;
      return idx != null ? this.registrants[idx] : null;
    },
    filteredSegments() {
      const current = this.currentRegistrant;
      const age = parseInt(current?.age, 10);
      if (!Number.isNaN(age) && age < 18) {
        // return this.segments.filter((s) => ["NON", "NDP"].includes(s.value));
        return this.segments.filter((s) => ["NON"].includes(s.value));
      }
      return this.segments;
    },
  },
  methods: {
    cancelEdit() {
      const idx = this.editing_index;
      if (idx === null) return;

      const reg = this.local_registrants[idx];
      if (!this.isFormComplete(reg)) {
        this.local_registrants.splice(idx, 1);
      } else {
        this.local_registrants[idx].is_summary = true;
      }

      this.editing_index = null;
    },
    isWaiverDisabled(registrant) {
      return !registrant.age;
    },
    requiresAccount(reg) {
      if (["MEM", "EMP", "DEP"].includes(reg.segment)) {
        return !!(reg.account_no || reg.serial_no);
      }
      return true;
    },
    openWaiver(index) {
      this.current_waiver_index = index;
      this.show_waiver = true;
    },
    onWaiverAgreed(waiver_data) {
      const i = this.current_waiver_index;
      if (i != null && this.registrants[i]) {
        this.registrants[i] = {
          ...this.registrants[i],
          guardian_name: waiver_data.guardian_name || "",
          relationship: waiver_data.relationship || "",
          signature: waiver_data.signature || "",
          has_signed_waiver: true,
          date_signed: this.timestamp,
          waiver: {
            ...this.registrants[i].waiver,
            ...waiver_data,
          },
        };
      }

      this.show_waiver = false;
      this.current_waiver_index = null;

      this.$emit("alert", {
        theme: "success",
        title: "Waiver Signed",
        message: "You have successfully signed the event waiver.",
      });
    },
    onWaiverClosed() {
      this.current_waiver_index = null;
      this.show_waiver = false;
    },
    mapGender(gender) {
      switch ((gender || "").toLowerCase()) {
        case "male":
          return "M";
        case "female":
          return "F";
        case "rather not say":
          return "X";
        default:
          return "";
      }
    },
    onPolicyClosed() {
      this.show_terms = false;
      this.$router.push("/dashboard");
    },
    fetchSegments() {
      this.$emit("showloader");
      FunRun.getSegment()
        .then((response) => {
          if (response?.data?.data) {
            const { segment, distance } = response.data.data;
            this.timestamp = response.data.timestamp;

            this.segments = segment.map((seg) => ({
              label: seg.label,
              value: seg.value,
            }));

            const distance_map = {};
            distance.forEach((item) => {
              const key = Object.keys(item)[0];
              distance_map[key] = item[key];
            });

            this.$store.state.fun_run.segments = this.segments;
            this.$store.state.fun_run.distances = distance_map;

            if (this.$store.state.fun_run.registrants?.length > 0) {
              this.registrants = [...this.$store.state.fun_run.registrants];
              this.show_data_privacy = false;
              this.$emit("hideloader");
              return;
            }

            FunRun.hasRegistered(this.$store.state.uuid)
              .then((response) => {
                this.has_registered = response.data.data.has_registered;
              })
              .catch((error) => this.$emit("error", error))
              .finally(() => {
                if (
                  this.$store.state.fun_run.registrants &&
                  this.$store.state.fun_run.registrants.length > 0
                ) {
                  this.$emit("hideloader");
                  return;
                }

                const has_any_registrants =
                  this.registrants.length > 0 ||
                  (this.$store.state.fun_run.registrants?.length ?? 0) > 0;

                if (!has_any_registrants) {
                  if (!this.has_registered) {
                    this.addSelf();
                  } else {
                    this.addRegistrant();
                  }
                }

                this.$emit("hideloader");
              });
          } else {
            this.$emit("hideloader");
            this.$emit("alert", {
              theme: "error",
              title: this.lang("lbl_0000058"),
              message: "No segments found.",
            });
          }
        })
        .catch((error) => this.$emit("error", error));
    },
    clearSegmentRelatedFields(reg) {
      reg.branch_service = "";
      reg.sponsor_type = "";
    },
    showBranchService(segment_value) {
      return ["MEM", "EMP"].includes(segment_value);
    },
    getBranchServicesBySegment(segment_value) {
      switch (segment_value) {
        case "MEM":
          return [
            { label: "PHILIPPINE ARMY", value: "PA" },
            { label: "PHILIPPINE NAVY", value: "PN" },
            { label: "PHILIPPINE AIR FORCE", value: "PAF" },
            { label: "RETIREE", value: "RET" },
            { label: "DEPENDENT", value: "DEP" },
            { label: "CIVILIAN EMPLOYEE", value: "CE" },
          ];
        case "EMP":
          return [
            { label: "MAIN OFFICE", value: "HO" },
            { label: "PSO", value: "PSO" },
          ];
        default:
          return [];
      }
    },
    showSponsorType(segment_value) {
      return segment_value === "SPR";
    },
    getSponsorTypes() {
      return [
        { label: "PLATINUM", value: "P" },
        { label: "GOLD", value: "G" },
        { label: "SILVER", value: "S" },
        { label: "BRONZE", value: "B" },
      ];
    },
    getDistanceBySegment(segment_value) {
      const distances =
        this.$store.state.fun_run.distances?.[segment_value] || [];

      return distances.map((d) => ({
        label: d.slot ? d.label : `${d.label} - NO SLOT AVAILABLE`,
        value: d.value,
        disabled: !d.slot,
      }));
    },
    getLabel(options, value) {
      const found = options.find((o) => o.value === value);
      return found ? found.label : "";
    },
    getDistanceLabel(segment, value) {
      const distances = this.$store.state.fun_run.distances?.[segment] || [];
      const found = distances.find((d) => d.value === value);
      return found ? found.label : "";
    },

    isFormComplete(reg) {
      if (!reg) return false;

      const required_fields = [
        reg.fname,
        reg.lname,
        reg.email,
        reg.mobile,
        reg.gender,
        reg.segment,
        reg.category,
      ];

      if (["MEM", "EMP"].includes(reg.segment)) {
        if (!reg.account_no && !reg.serial_no) return false;
        required_fields.push(reg.branch_service);
      }

      if (reg.segment === "DEP") {
        if (!reg.account_no && !reg.serial_no) return false;
      }

      if (reg.segment === "SPR") {
        required_fields.push(reg.sponsor_type);
      }

      const is_complete = required_fields.every(Boolean);

      return reg.category === "2" ? is_complete : is_complete && reg.distance;
    },
    addSelf() {
      let state = this.$store.state;
      let mobile = state.info.mobile_no || "";

      if (mobile.startsWith("+63")) {
        mobile = mobile.slice(3);
      }

      this.registrants.push({
        account_no: state.info.account_no || "",
        serial_no: state.info.serial_no || "",
        age: state.info.age != null ? String(state.info.age) : "",
        branch_service: "",
        category: "",
        distance: "",
        email: state.info.email || "",
        fname: state.info.first_name || "",
        gender: this.mapGender(state.info.gender),
        lname: state.info.last_name || "",
        mname: state.info.middle_name || "",
        mobile: mobile,
        segment: "",
        sponsor_type: "",
        is_summary: false,
        date_signed: this.timestamp,
        has_signed_waiver: false,
        waiver: {
          user_name: "",
          guardian_name: "",
          relationship: "",
          signature: "",
          is_minor: false,
          checks: [false, false, false, false],
        },
      });

      this.editing_index = this.registrants.length - 1;
    },
    addRegistrant() {
      if (this.editing_index !== null) return;

      if(this.registrants.length == 3) {
        this.$emit("alert", {
          theme: "error",
          title: this.lang("lbl_0000034"),
          message: this.lang("lbl_0000344"),
        });
        return;
      }

      const new_registrant = {
        account_no: "",
        serial_no: "",
        age: "",
        branch_service: "",
        category: "",
        distance: "",
        email: "",
        fname: "",
        gender: "",
        lname: "",
        mname: "",
        mobile: "",
        segment: "",
        sponsor_type: "",
        is_summary: false,
        date_signed: this.timestamp,
        has_signed_waiver: false,
        waiver: {
          user_name: "",
          guardian_name: "",
          relationship: "",
          signature: "",
          is_minor: false,
          checks: [false, false, false],
        },
      };

      this.registrants.push(new_registrant);
      this.editing_index = this.registrants.length - 1;
    },
    editRegistrant(index) {
      this.registrants.forEach((r) => (r.is_summary = true));
      this.editing_index = index;
      this.registrants[index].is_summary = false;
    },

    deleteRegistrant(index) {
      const reg = this.registrants[index];
      if (!reg) return;

      const is_guardian = this.registrants.some(
        (r, i) =>
          i !== index &&
          r.waiver?.guardian_name &&
          r.waiver.guardian_name.trim().toLowerCase() ===
            `${reg.fname} ${reg.lname}`.trim().toLowerCase()
      );

      if (is_guardian) {
        this.$emit("alert", {
          theme: "error",
          title: this.lang("lbl_0000034"),
          message: `${reg.fname} ${reg.lname} cannot be deleted because this person is listed as a guardian for another registrant.`,
        });
        return;
      }

      this.registrants.splice(index, 1);
    },
    registerAll() {
      this.$store.state.fun_run.registrants = this.registrants.map((r) => ({
        ...r,
        guardian_name: r.guardian_name || "",
        relationship: r.relationship || "",
        signature: r.signature || "",
        date_signed: r.date_signed || this.timestamp,
      }));

      if (!this.$store.state.fun_run.is_editing) {
        this.$router.push("/fun-run/safety-reminders");
      } else {
        this.$router.push("/fun-run/summary");
      }
    },
    saveChanges(index) {
      const reg = this.local_registrants[index];
      if (!reg || !this.isFormComplete(reg)) {
        return this.$emit("alert", {
          theme: "error",
          title: this.lang("lbl_0000058"),
          message: this.lang("lbl_0000321"),
        });
      }

      this.local_registrants[index].is_summary = true;
      this.editing_index = null;

      this.$store.state.fun_run.registrants = [...this.local_registrants];
    },
  },
};
</script>
